/**
 * TransactionTable Component
 *
 * Main spreadsheet-style table component for displaying transactions.
 * Features sticky header, column sorting, row selection, and keyboard navigation.
 *
 * @see types/accounting.ts for Transaction and Account types
 */

import { useCallback, useRef, useState, useEffect, Fragment } from 'react';
import clsx from 'clsx';
import { ChevronUp, ChevronDown } from 'lucide-react';
import type { Transaction, Account } from '@/types/accounting';
import type { Tag } from '@/types/accountingTags';
import { TransactionRow } from './TransactionRow';
import { SplitDetailsPanel } from './SplitDetailsPanel';

export interface TransactionTableProps {
  /** List of transactions to display */
  transactions: Transaction[];
  /** Flat list of all accounts */
  accounts: Account[];
  /** Hierarchical tree of accounts */
  accountTree: Account[];
  /** Set of selected transaction IDs */
  selectedIds: Set<number>;
  /** Callback when selection changes */
  onSelectionChange: (ids: Set<number>) => void;
  /** Callback when a transaction is classified */
  onClassify: (id: number, accountId: number, owner: string) => void;
  /** Callback when a transaction is clicked for review (opens ReviewPanel) */
  onReview?: (transaction: Transaction) => void;
  /** Currently sorted column key */
  sortColumn?: string;
  /** Sort direction */
  sortDirection?: 'asc' | 'desc';
  /** Callback when a column header is clicked for sorting */
  onSort?: (column: string) => void;
  /** All available tags for the dropdown (optional for backward compatibility) */
  availableTags?: Tag[];
  /** Callback when a tag is added (optional for backward compatibility) */
  onAddTag?: (transactionId: number, tag: Tag) => Promise<void>;
  /** Callback when a tag is removed (optional for backward compatibility) */
  onRemoveTag?: (transactionId: number, tagging: { tag: { tagType: string; value: string } }) => Promise<void>;
}

/**
 * Column configuration for the transaction table
 */
interface ColumnConfig {
  key: string;
  label: string;
  width?: string;
  align?: 'left' | 'right' | 'center';
  sortable?: boolean;
  sticky?: boolean;
}

/**
 * Check if a transaction is a split (has multiple debit entries)
 */
function isSplitTransaction(transaction: Transaction): boolean {
  return transaction.entries.filter((e) => e.entryType === 'debit').length > 1;
}

const columns: ColumnConfig[] = [
  { key: 'select', label: '', width: 'w-10', sticky: true },
  { key: 'date', label: 'Date', width: 'w-24', sortable: true },
  { key: 'description', label: 'Description', sortable: true },
  { key: 'amount', label: 'Amount', width: 'w-28', align: 'right', sortable: true },
  { key: 'category', label: 'Category', width: 'w-48' },
  { key: 'owner', label: 'Owner', width: 'w-20' },
  { key: 'tags', label: 'Tags', width: 'w-48' },
  { key: 'status', label: 'Status', width: 'w-32' },
  { key: 'actions', label: '', width: 'w-20' },
];

/**
 * TransactionTable renders a full-featured table of transactions.
 *
 * Features:
 * - Sticky header row that stays visible during scroll
 * - Column sorting by clicking sortable column headers
 * - Row selection via checkboxes
 * - Select all / deselect all via header checkbox
 * - Keyboard navigation with arrow keys
 * - Row hover highlighting
 *
 * Keyboard Navigation:
 * - Arrow Up/Down: Move focus between rows
 * - Enter/Space: Select/click focused row
 * - Ctrl+A: Select all rows (when table has focus)
 *
 * @example
 * <TransactionTable
 *   transactions={transactions}
 *   accounts={accounts}
 *   accountTree={accountTree}
 *   selectedIds={selectedIds}
 *   onSelectionChange={setSelectedIds}
 *   onClassify={handleClassify}
 *   sortColumn="date"
 *   sortDirection="desc"
 *   onSort={handleSort}
 * />
 */
export function TransactionTable({
  transactions,
  accounts: _accounts, // Reserved for future classification feature
  accountTree: _accountTree, // Reserved for future classification feature
  selectedIds,
  onSelectionChange,
  onClassify: _onClassify, // Reserved for future classification feature
  onReview,
  sortColumn,
  sortDirection,
  onSort,
  availableTags,
  onAddTag,
  onRemoveTag,
}: TransactionTableProps) {
  // Suppress unused variable warnings - these props will be used in future iterations
  void _accounts;
  void _accountTree;
  void _onClassify;
  const tableRef = useRef<HTMLTableElement>(null);
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const [expandedSplitIds, setExpandedSplitIds] = useState<Set<number>>(new Set());

  // Handle toggle expansion of split transactions
  const handleToggleExpand = useCallback((id: number) => {
    setExpandedSplitIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // Track if all rows are selected
  const allSelected = transactions.length > 0 && transactions.every((t) => selectedIds.has(t.id));
  const someSelected = transactions.some((t) => selectedIds.has(t.id)) && !allSelected;

  // Handle select all checkbox
  const handleSelectAll = useCallback(() => {
    if (allSelected) {
      // Deselect all
      onSelectionChange(new Set());
    } else {
      // Select all
      onSelectionChange(new Set(transactions.map((t) => t.id)));
    }
  }, [allSelected, transactions, onSelectionChange]);

  // Handle individual row selection
  const handleRowSelect = useCallback(
    (id: number, selected: boolean) => {
      const newSelected = new Set(selectedIds);
      if (selected) {
        newSelected.add(id);
      } else {
        newSelected.delete(id);
      }
      onSelectionChange(newSelected);
    },
    [selectedIds, onSelectionChange]
  );

  // Handle row focus
  const handleRowFocus = useCallback((index: number) => {
    setFocusedIndex(index);
  }, []);

  // Handle row click
  const handleRowClick = useCallback(
    (transaction: Transaction) => {
      // Open review panel for any transaction if onReview is provided
      if (onReview) {
        onReview(transaction);
      } else {
        // Toggle selection on click
        handleRowSelect(transaction.id, !selectedIds.has(transaction.id));
      }
    },
    [handleRowSelect, selectedIds, onReview]
  );

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTableElement>) => {
      if (transactions.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setFocusedIndex((prev) =>
            prev < transactions.length - 1 ? prev + 1 : prev
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setFocusedIndex((prev) => (prev > 0 ? prev - 1 : 0));
          break;
        case 'Home':
          e.preventDefault();
          setFocusedIndex(0);
          break;
        case 'End':
          e.preventDefault();
          setFocusedIndex(transactions.length - 1);
          break;
        case 'a':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            handleSelectAll();
          }
          break;
      }
    },
    [transactions.length, handleSelectAll]
  );

  // Focus the appropriate row when focusedIndex changes
  useEffect(() => {
    if (focusedIndex >= 0 && tableRef.current) {
      const rows = tableRef.current.querySelectorAll('[data-testid="transaction-row"]');
      const row = rows[focusedIndex] as HTMLElement;
      if (row) {
        row.focus();
      }
    }
  }, [focusedIndex]);

  // Handle column header click for sorting
  const handleHeaderClick = (column: string, sortable?: boolean) => {
    if (sortable && onSort) {
      onSort(column);
    }
  };

  // Render sort indicator
  const renderSortIndicator = (columnKey: string) => {
    if (sortColumn !== columnKey) {
      return null;
    }

    return sortDirection === 'asc' ? (
      <ChevronUp size={14} className="inline-block ml-1" />
    ) : (
      <ChevronDown size={14} className="inline-block ml-1" />
    );
  };

  // Empty state
  if (transactions.length === 0) {
    return (
      <div
        className="text-center py-12 text-gray-500"
        data-testid="empty-state"
      >
        <p className="text-lg">No transactions found</p>
        <p className="text-sm mt-2">
          Try adjusting your filters or adding new transactions.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto overflow-y-visible">
      <table
        ref={tableRef}
        className="min-w-full divide-y divide-gray-200"
        onKeyDown={handleKeyDown}
        role="grid"
        aria-label="Transactions table"
        data-testid="transaction-table"
      >
        <thead className="bg-gray-50 sticky top-0 z-10">
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                className={clsx(
                  'px-3 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider',
                  column.width,
                  {
                    'text-left': column.align !== 'right' && column.align !== 'center',
                    'text-right': column.align === 'right',
                    'text-center': column.align === 'center',
                    'cursor-pointer hover:bg-gray-100 select-none': column.sortable,
                    'sticky left-0 bg-gray-50': column.sticky,
                  }
                )}
                onClick={() => handleHeaderClick(column.key, column.sortable)}
                aria-sort={
                  sortColumn === column.key
                    ? sortDirection === 'asc'
                      ? 'ascending'
                      : 'descending'
                    : undefined
                }
              >
                {column.key === 'select' ? (
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(el) => {
                      if (el) {
                        el.indeterminate = someSelected;
                      }
                    }}
                    onChange={handleSelectAll}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    aria-label="Select all transactions"
                  />
                ) : (
                  <>
                    {column.label}
                    {renderSortIndicator(column.key)}
                  </>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {transactions.map((transaction, index) => (
            <Fragment key={transaction.id}>
              <TransactionRow
                transaction={transaction}
                isSelected={selectedIds.has(transaction.id)}
                isFocused={focusedIndex === index}
                isExpanded={expandedSplitIds.has(transaction.id)}
                onSelect={(selected) => handleRowSelect(transaction.id, selected)}
                onFocus={() => handleRowFocus(index)}
                onClick={() => handleRowClick(transaction)}
                availableTags={availableTags}
                onAddTag={onAddTag}
                onRemoveTag={onRemoveTag}
                onToggleExpand={() => handleToggleExpand(transaction.id)}
              />
              {isSplitTransaction(transaction) && expandedSplitIds.has(transaction.id) && (
                <tr>
                  <td colSpan={columns.length} className="p-0">
                    <SplitDetailsPanel entries={transaction.entries} />
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default TransactionTable;
